<template>
  <section id="dashboard" class="dashboard-all-container">
    <!-- Progressive Stats Component (Overlay) -->
    <progressiveStats 
      :stats="progressState" 
      :startTime="loadStartTime"
      :allowCancel="true"
      @cancel="cancelProgressiveLoad"
    />

    <div v-if="userData.roles[0].id == 1">
      <superAdmin
        :isLoading="isLoading"
        :userData="userData"
        :dataDashboard="dataDashboard"
        :insuranceCharts="insuranceCharts"
        @buscar="buscar($event)"
        @export-csv="exportDashboardCsv"
        @export-excel="exportDashboardExcel"
      />
    </div>
    <div v-else>
      <admin
        :isLoading="isLoading"
        :userData="userData"
        :dataDashboard="dataDashboard"
        :insuranceCharts="insuranceCharts"
        @buscar="buscar($event)"
        @export-csv="exportDashboardCsv"
        @export-excel="exportDashboardExcel"
      />
    </div>
  </section>
</template>

<script>
import { loadAllWithInsurance } from "@/core/services/api/dashboard/loadAllWithInsurance";
import { daysBetween } from "@/core/utils/dashboardDates";
import superAdmin from "./superAdmin/superAdmin.vue";
import admin from "./admin/admin.vue";
import progressiveStats from "./components/progressiveStats.vue";
import { isMobileDevice, getCurrentConfig } from "./config/mobile.config";
import {
  exportDashboardToCsv,
  exportDashboardToExcel,
} from "./utils/dashboardExport";
import { recalculateIncomeAverages } from "./utils/totals";
// Import mobile optimizations CSS
import "./assets/mobileComponentOptimizations.css";

export default {
  components: {
    admin,
    superAdmin,
    progressiveStats,
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("userData")),
      dataDashboard: {},
      insuranceCharts: {
        ins_base_value: null,
        year_data: [],
      },
      isLoading: false,
      progressState: {
        current: 0,
        total: 0,
        percentage: 0,
        currentMonth: 0,
        currentYear: 0,
      },
      loadStartTime: 0,
      currentLoadAbortController: null,
      mobileConfig: null,
      hasSearchResults: false,
      pendingSearchMeta: null,
      lastSearchContext: null,
    };
  },
  computed: {
    canExport() {
      return (
        this.hasSearchResults &&
        !!this.lastSearchContext &&
        this.dataDashboard &&
        Object.keys(this.dataDashboard).length > 0
      );
    },
  },
  created() {
    // Obtener configuración móvil
    this.mobileConfig = getCurrentConfig();
    
    // Eliminamos la búsqueda automática para que solo se ejecute cuando el usuario presione buscar
    // if (this.userData.roles[0].id != 2) {
    //   this.buscar({ 
    //     start: `${new Date().getFullYear()}-${new Date().getMonth() + 1}-1`, 
    //     end: `${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}` 
    //   }); 
    // }
  },
  mounted() {
    // Escuchar cambios de tamaño de ventana para actualizar configuración
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    // Limpiar event listener
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      // Actualizar configuración cuando cambie el tamaño de la ventana
      this.mobileConfig = getCurrentConfig();
    },
    
    async buscar({ start, end, facilities }) {
      if (this.currentLoadAbortController) {
        this.currentLoadAbortController.abort();
      }
      const controller = new AbortController();
      this.currentLoadAbortController = controller;

      this.pendingSearchMeta = { start, end, facilities };
      this.isLoading = true;
      this.loadStartTime = Date.now();

      const userId =
        this.userData.roles[0].id != 1 && this.userData.roles[0].id != 2
          ? this.userData.id
          : null;
      const maxDays = this.mobileConfig.progressiveLoading.maxDays;
      const useProgressive = daysBetween(start, end) > maxDays && !isMobileDevice();

      try {
        const result = await loadAllWithInsurance({
          start,
          end,
          facilities,
          userId,
          signal: controller.signal,
          useProgressive,
          onProgress: progress => {
            if (this.currentLoadAbortController !== controller) return;
            this.progressState = progress;
          },
        });

        if (this.currentLoadAbortController !== controller) return;

        if (result.cash) this.processDashboardData(result.cash);
        if (result.insurance) {
          this.insuranceCharts = {
            ins_base_value: result.insurance.ins_base_value || null,
            year_data: Array.isArray(result.insurance.year_data)
              ? result.insurance.year_data
              : [],
          };
        } else if (result.insuranceError) {
          this.insuranceCharts = { ins_base_value: null, year_data: [] };
        }

        if (useProgressive) {
          this.progressState = {
            ...this.progressState,
            percentage: 100,
          };
        }

        if (result.cashError) this.handleError(result.cashError);
        if (result.insuranceError) {
          this.$bvToast.toast(
            "No se pudieron cargar las gráficas de seguro.",
            {
              title: "Insurance charts",
              variant: "danger",
              solid: true,
              autoHideDelay: 5000,
            }
          );
        }
      } catch (error) {
        if (error?.name === "AbortError" || this.currentLoadAbortController !== controller) {
          return;
        }
        console.error("Error general en búsqueda:", error);
        this.handleError(error);
      } finally {
        if (this.currentLoadAbortController === controller) {
          this.currentLoadAbortController = null;
          this.isLoading = false;
          if (this.progressState.percentage > 0 && this.progressState.percentage < 100) {
            this.progressState = {
              current: 0,
              total: 0,
              percentage: 0,
              currentMonth: 0,
              currentYear: 0,
            };
          }
        }
      }
    },

    cancelProgressiveLoad() {
      if (this.currentLoadAbortController) {
        this.currentLoadAbortController.abort();
        this.currentLoadAbortController = null;
      }
      this.isLoading = false;
      this.progressState = {
        current: 0,
        total: 0,
        percentage: 0,
        currentMonth: 0,
        currentYear: 0,
      };
    },
    
    processDashboardData(data) {
      if (data.allDataCount && data.all_patients) {
        this.dataDashboard = {
          ...data.allDataCount.data,
          ...data.all_patients.data,
          ...data.bonus.data,
          ...data.total_refunds.data,
          ...data.values_totals,
          ...data.year_data.data,
        }
      } else {
        this.dataDashboard = data
      }

      this.lastSearchContext = this.pendingSearchMeta
        ? { ...this.pendingSearchMeta }
        : null;
      this.hasSearchResults = true;

      if (this.pendingSearchMeta?.start && this.pendingSearchMeta?.end) {
        recalculateIncomeAverages(
          this.dataDashboard,
          this.pendingSearchMeta.start,
          this.pendingSearchMeta.end
        );
      }
      
      this.isLoading = false;
    },

    exportDashboardCsv() {
      if (!this.canExport || !this.lastSearchContext) {
        this.$bvToast.toast(
          "Run a search with the filter and click Search before exporting.",
          {
            title: "Export unavailable",
            variant: "danger",
            solid: true,
            autoHideDelay: 5000,
          }
        );
        return;
      }
      try {
        exportDashboardToCsv(this.dataDashboard, this.lastSearchContext);
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("Could not generate the CSV file.", {
          title: "Error",
          variant: "danger",
          solid: true,
          autoHideDelay: 4000,
        });
      }
    },

    exportDashboardExcel() {
      if (!this.canExport || !this.lastSearchContext) {
        this.$bvToast.toast(
          "Run a search with the filter and click Search before exporting.",
          {
            title: "Export unavailable",
            variant: "danger",
            solid: true,
            autoHideDelay: 5000,
          }
        );
        return;
      }
      try {
        exportDashboardToExcel(this.dataDashboard, this.lastSearchContext);
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("Could not generate the Excel file.", {
          title: "Error",
          variant: "danger",
          solid: true,
          autoHideDelay: 4000,
        });
      }
    },
    
    handleError(error) {
      console.error('🚨 Error manejado:', error);
      this.isLoading = false;
      
      // Mostrar mensaje de error al usuario
      this.$bvToast.toast('Failed to load data. Please try again.', {
        title: 'Load error',
        variant: 'danger',
        solid: true,
        autoHideDelay: 5000
      });
    },
  },
};
</script>
